"use client";

/**
 * Session tabs (change add-gateway-tabs, design D13; refined by
 * fix-panel-session-agent-behavior D2/D3/D4).
 *
 * Tab bar with one view per remote session: xterm.js for ssh (the API is the
 * SSH client and the connector is the only egress), guacamole canvas for
 * rdp/vnc, plain iframe for web (with blocked-embed detection). Tabs
 * from multiple tenants coexist, distinguished by a border-bottom in the
 * tenant color. An "en segundo plano" bar offers re-attach via resume
 * tickets. Sessions never close on navigation: only the ✕ button closes,
 * and the bar persists as the header of every /app page.
 */

import type * as Guacamole from "@glokon/guacamole-common-js";
import { Columns } from "@phosphor-icons/react/dist/icons/Columns";
import { Rows } from "@phosphor-icons/react/dist/icons/Rows";
import { Square } from "@phosphor-icons/react/dist/icons/Square";
import { SidebarSimple } from "@phosphor-icons/react/dist/icons/SidebarSimple";
import { SquaresFour } from "@phosphor-icons/react/dist/icons/SquaresFour";
import { useTranslations } from "next-intl";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { usePathname, useRouter } from "next/navigation";

import { SshTerminal } from "@/components/ssh-terminal";
import { ensureWsLog } from "@/lib/wslog";
import type { ConnectionOut } from "@/lib/connections";
import type { ConnectorOut } from "@/lib/connectors";
import { loadTerminalPrefs, toSessionTerminal } from "@/lib/terminal-prefs";
import {
  closeSession,
  listSessions,
  openSession,
  type SessionCreated,
  type SessionOut,
} from "@/lib/sessions";

type Tab = {
  sessionId: string;
  ticket: string;
  connectionId: string;
  connectionName: string;
  type: string;
  tenantId: string;
  color: string;
  kind: "guac" | "web" | "term";
  url?: string;
  connectorName?: string;
  /** Connector this session was pinned to, when the user chose one (D6/D9). */
  viaConnectorId?: string;
  status: "connecting" | "open" | "closed" | "error";
  detail?: string;
};

type SessionLayout = "single" | "columns" | "rows" | "grid";

type TabsContextValue = {
  open: (conn: ConnectionOut, resume?: string, via?: ConnectorOut | null) => Promise<void>;
  tabs: Tab[];
  activeId: string | null;
  setActiveId: (id: string | null) => void;
  closeTab: (tab: Tab) => void;
  patchTab: (sessionId: string, status: Tab["status"], detail?: string) => void;
  retryTab: (tab: Tab) => void;
  /** Reopen a failed session pinned to `connector` (D9). */
  retryTabVia: (tab: Tab, connector: ConnectorOut) => void;
  resumeTab: (tab: Tab) => void;
};

const TabsContext = createContext<TabsContextValue | null>(null);

export function useSessionTabs(): TabsContextValue {
  const value = useContext(TabsContext);
  if (!value) throw new Error("useSessionTabs requires <SessionTabsProvider>");
  return value;
}

const WS_BASE = process.env.NEXT_PUBLIC_WS_BASE ?? "";

function wsUrl(): string {
  const base = WS_BASE
    ? WS_BASE
    : `${window.location.protocol === "https:" ? "wss" : "ws"}://${window.location.host}`;
  return `${base.replace(/\/$/, "")}/api/ws/session`;
}

// Tenant colors arrive with GET /sessions; cached in a module map so new
// tabs get the right border color immediately.
const tenantColors = new Map<string, string>();

function tenantColor(tenantId: string): string {
  return tenantColors.get(tenantId) ?? "#6366f1";
}

/**
 * D3 keyboard capture: forwards the keysym to the session (key-down) and
 * always returns false (consumes the key → the library preventDefaults it)
 * so no browser shortcut competes with the remote session while it holds
 * focus. Pure unit tested in session-tabs.test.tsx.
 */
/**
 * Close codes the API may close a session WS with. 4405-4408 come from the
 * target-failure taxonomy (fix-session-egress-and-clipboard D3): the server
 * classifies the guacd error and puts the reason in the close frame, so the
 * browser never shows guacd's free text.
 */
export const SESSION_ERROR_CODES = [
  4401, 4403, 4404, 4405, 4406, 4407, 4408, 4412, 4413, 4414, 1011,
];

export function errorCodeKey(code: number): string | null {
  return SESSION_ERROR_CODES.includes(code) ? `wsError.${code}` : null;
}

/**
 * Guacamole runtime, deferred (optimize-web-heavy-bundles D1). The types are
 * erased at build time; the module is pulled in only in the browser, and only
 * when a GuacSession mounts, so no route carries it in its initial graph. Same
 * pattern as loadXterm in ssh-terminal.tsx.
 */
type GuacamoleModules = typeof import("@glokon/guacamole-common-js");

let guacamoleModules: Promise<GuacamoleModules> | null = null;

function loadGuacamole(): Promise<GuacamoleModules> {
  guacamoleModules ??= import("@glokon/guacamole-common-js");
  return guacamoleModules;
}

export function handleSessionKey(
  keysym: number,
  send: (down: 0 | 1, keysym: number) => void,
): boolean {
  send(1, keysym);
  return false;
}

/** Guacamole keysym for one character (Unicode keysyms use 0x01000000+cp). */
export function charKeysym(ch: string): number {
  const cp = ch.codePointAt(0) ?? 0;
  return cp <= 0xff ? cp : 0x01000000 + cp;
}

const NAMED_KEYSYMS: Record<string, number> = {
  Backspace: 0xff08,
  Tab: 0xff09,
  Enter: 0xff0d,
  Escape: 0xff1b,
  Delete: 0xffff,
  Insert: 0xff63,
  Home: 0xff50,
  End: 0xff57,
  PageUp: 0xff55,
  PageDown: 0xff56,
  ArrowLeft: 0xff51,
  ArrowUp: 0xff52,
  ArrowRight: 0xff53,
  ArrowDown: 0xff54,
};

/** Keysym for a DOM `KeyboardEvent.key`; null means "let input handle it"
 * (mobile IMEs report `Unidentified` and deliver the text via `input`). */
export function keyNameKeysym(key: string): number | null {
  const chars = [...key];
  if (chars.length === 1) return charKeysym(chars[0]);
  return NAMED_KEYSYMS[key] ?? null;
}

export function SessionTabsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const t = useTranslations("sessions");
  const [tabs, setTabs] = useState<Tab[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const tabsRef = useRef<Tab[]>([]);
  useEffect(() => {
    tabsRef.current = tabs;
  }, [tabs]);

  const open = useCallback(
    async (conn: ConnectionOut, resume?: string, via?: ConnectorOut | null) => {
      // D4: same connection already open → focus the existing tab instead
      // of opening a duplicated session.
      const existing = tabsRef.current.find(
        (tab) =>
          tab.connectionId === conn.id &&
          (tab.status === "connecting" || tab.status === "open"),
      );
      if (existing) {
        setActiveId(existing.sessionId);
        return;
      }
      let created: SessionCreated;
      try {
        created = await openSession(
          conn.id,
          resume,
          conn.type === "ssh" ? toSessionTerminal(loadTerminalPrefs()) : undefined,
          via?.id,
        );
      } catch (e) {
        const status = (e as { status?: number }).status;
        const code = (e as { code?: string }).code;
        if (status === 409 || (status === 403 && code === "quota_exceeded")) {
          window.alert(t("sessionLimit"));
        } else {
          window.alert(t("openFailed", { status: String(status ?? "error") }));
        }
        return;
      }
      const isWeb = created.connection.type === "web";
      setTabs((prev) => {
        if (prev.some((t) => t.sessionId === created.session_id)) {
          return prev.map((t) =>
            t.sessionId === created.session_id
              ? {
                  ...t,
                  ticket: created.ticket,
                  status: "connecting" as const,
                  connectorName: via?.name,
                  viaConnectorId: via?.id,
                }
              : t,
          );
        }
        return [
          ...prev,
          {
            sessionId: created.session_id,
            ticket: created.ticket,
            connectionId: conn.id,
            connectionName: created.connection.name,
            type: created.connection.type,
            tenantId: conn.tenant_id,
            color: tenantColor(conn.tenant_id),
            kind: isWeb ? "web" : created.connection.type === "ssh" ? "term" : "guac",
            url: isWeb ? String(conn.params?.url ?? "") : undefined,
            connectorName: via?.name,
            viaConnectorId: via?.id,
            status: "connecting",
          },
        ];
      });
      setActiveId(created.session_id);
    },
    [t],
  );

  const closeTab = useCallback((tab: Tab) => {
    void (async () => {
      await closeSession(tab.sessionId).catch(() => undefined);
      setTabs((prev) => prev.filter((t) => t.sessionId !== tab.sessionId));
    })();
  }, []);

  const patchTab = useCallback(
    (sessionId: string, status: Tab["status"], detail?: string) => {
      setTabs((prev) =>
        prev.map((t) =>
          t.sessionId === sessionId ? { ...t, status, detail } : t,
        ),
      );
    },
    [],
  );

  const retryTab = useCallback(
    (tab: Tab) => {
      const conn: ConnectionOut = {
        id: tab.connectionId,
        tenant_id: tab.tenantId,
        folder_id: null,
        name: tab.connectionName,
        type: tab.type as ConnectionOut["type"],
        host: "",
        port: null,
        params: tab.kind === "web" && tab.url ? { url: tab.url } : null,
        credential_id: null,
        agent_id: null,
      };
      closeTab(tab);
      void open(conn);
    },
    [closeTab, open],
  );

  // D9: the failed session is replaced by a new one pinned to the connector the
  // user picked. The preference is not written here: it is written by the new
  // session itself once it opens, because an attempt that fails says nothing
  // about the route.
  const retryTabVia = useCallback(
    (tab: Tab, connector: ConnectorOut) => {
      const conn: ConnectionOut = {
        id: tab.connectionId,
        tenant_id: tab.tenantId,
        folder_id: null,
        name: tab.connectionName,
        type: tab.type as ConnectionOut["type"],
        host: "",
        port: null,
        params: tab.kind === "web" && tab.url ? { url: tab.url } : null,
        credential_id: null,
        agent_id: null,
      };
      closeTab(tab);
      void open(conn, undefined, connector);
    },
    [closeTab, open],
  );

  const resumeTab = useCallback(
    async (tab: Tab) => {
      // Reattach to the SAME server session with a fresh ticket (used on
      // view remount after page navigation; never replays a ticket).
      let created: SessionCreated;
      try {
        created = await openSession(
          tab.connectionId,
          tab.sessionId,
          tab.type === "ssh" ? toSessionTerminal(loadTerminalPrefs()) : undefined,
        );
      } catch {
        patchTab(tab.sessionId, "error", t("openFailed", { status: "error" }));
        return;
      }
      setTabs((prev) =>
        prev.map((x) =>
          x.sessionId === tab.sessionId
            ? { ...x, ticket: created.ticket, status: "connecting" as const }
            : x,
        ),
      );
      setActiveId(tab.sessionId);
    },
    [t, patchTab],
  );

  const value = useMemo(
    () => ({
      open,
      tabs,
      activeId,
      setActiveId,
      closeTab,
      patchTab,
      retryTab,
      retryTabVia,
      resumeTab,
    }),
    [open, tabs, activeId, closeTab, patchTab, retryTab, retryTabVia, resumeTab],
  );

  return <TabsContext.Provider value={value}>{children}</TabsContext.Provider>;
}

/**
 * Global tab bar (change fix-app-shell-ux; refined by
 * fix-panel-session-agent-behavior D2/D3). Rendered by AppShell from the
 * /app layout so it persists across navigation. `showCanvas` hides the
 * session canvas while the session socket stays alive; the bar handles
 * navigation itself (D4: clicking a tab outside its detail navigates
 * there; the ✕ always closes).
 */
export function SessionTabsBar({
  showCanvas = true,
  detailVisible = true,
  onToggleDetail,
}: {
  showCanvas?: boolean;
  detailVisible?: boolean;
  onToggleDetail?: () => void;
}) {
  const { tabs, activeId, setActiveId, closeTab, open, patchTab, retryTab, retryTabVia } =
    useSessionTabs();
  const t = useTranslations("sessions");
  const router = useRouter();
  const pathname = usePathname();
  const [layout, setLayout] = useState<SessionLayout>("single");
  const [columnRatio, setColumnRatio] = useState(0.5);
  const [rowRatio, setRowRatio] = useState(0.5);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const activeTab = tabs.find((tab) => tab.sessionId === activeId) ?? null;

  // Derived, not stored: closing tabs must not leave a layout with empty
  // cells active. A stored grid falls back to columns until a third tab
  // exists again.
  const effectiveLayout: SessionLayout =
    tabs.length < 2
      ? "single"
      : layout === "grid" && tabs.length < 3
        ? "columns"
        : layout;
  const capacity =
    effectiveLayout === "single" ? 1 : effectiveLayout === "grid" ? 4 : 2;
  let visibleTabs =
    effectiveLayout === "single"
      ? activeTab
        ? [activeTab]
        : []
      : tabs.slice(0, capacity);
  if (
    effectiveLayout !== "single" &&
    activeTab &&
    !visibleTabs.some((tab) => tab.sessionId === activeTab.sessionId)
  ) {
    visibleTabs = [
      activeTab,
      ...tabs.filter((tab) => tab.sessionId !== activeTab.sessionId),
    ].slice(0, capacity);
  }
  const visibleIds = new Set(visibleTabs.map((tab) => tab.sessionId));
  const panePlacement = (index: number): React.CSSProperties => {
    if (effectiveLayout === "columns") {
      return { gridColumn: index === 0 ? "1" : "3", gridRow: "1" };
    }
    if (effectiveLayout === "rows") {
      return { gridColumn: "1", gridRow: index === 0 ? "1" : "3" };
    }
    if (effectiveLayout === "grid") {
      return {
        gridColumn: index % 2 === 0 ? "1" : "3",
        gridRow: index < 2 ? "1" : "3",
      };
    }
    return {};
  };
  const gridStyle: React.CSSProperties =
    effectiveLayout === "single"
      ? {}
      : {
          height: "var(--session-pane-height, 70vh)",
          gridTemplateColumns:
            effectiveLayout === "rows"
              ? "minmax(0, 1fr)"
              : `minmax(0, ${columnRatio}fr) 8px minmax(0, ${1 - columnRatio}fr)`,
          gridTemplateRows:
            effectiveLayout === "columns"
              ? "minmax(0, 1fr)"
              : `minmax(0, ${rowRatio}fr) 8px minmax(0, ${1 - rowRatio}fr)`,
        };
  const layoutButtonClass = (active: boolean) =>
    "rounded p-1 " +
    (active
      ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900"
      : "text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800");

  function clampRatio(value: number) {
    return Math.min(0.8, Math.max(0.2, value));
  }

  function startColumnResize(event: React.PointerEvent<HTMLDivElement>) {
    event.preventDefault();
    const startX = event.clientX;
    const startRatio = columnRatio;
    const width = gridRef.current?.clientWidth ?? 0;
    if (!width) return;
    const onMove = (moveEvent: PointerEvent) => {
      setColumnRatio(clampRatio(startRatio + (moveEvent.clientX - startX) / width));
    };
    const stop = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", stop);
      document.documentElement.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.documentElement.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", stop);
  }

  function startRowResize(event: React.PointerEvent<HTMLDivElement>) {
    event.preventDefault();
    const startY = event.clientY;
    const startRatio = rowRatio;
    const height = gridRef.current?.clientHeight ?? 0;
    if (!height) return;
    const onMove = (moveEvent: PointerEvent) => {
      setRowRatio(clampRatio(startRatio + (moveEvent.clientY - startY) / height));
    };
    const stop = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", stop);
      document.documentElement.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.documentElement.style.cursor = "row-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", stop);
  }

  const selectTab = (tab: Tab) => {
    setActiveId(tab.sessionId);
    const detail = `/app/connections/${tab.connectionId}`;
    if (pathname !== detail) router.push(detail);
  };

  if (tabs.length === 0) return <ActiveSessionsBar onAttach={open} />;
  return (
    <section
      aria-label={t("aria")}
      className="rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900"
    >
      <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-2 pt-2 dark:border-slate-800">
        <div className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto">
          {tabs.map((tab) => (
            <div
              key={tab.sessionId}
              className={
                "flex items-center gap-2 rounded-t-md border border-b-0 px-3 py-1.5 text-sm " +
                (activeTab?.sessionId === tab.sessionId
                  ? "border-slate-300 bg-slate-50 dark:border-slate-700 dark:bg-slate-800"
                  : "border-transparent hover:bg-slate-100 dark:hover:bg-slate-800/60")
              }
              style={{ borderBottom: `3px solid ${tab.color}` }}
            >
              <button
                type="button"
                className="font-medium text-slate-700 dark:text-slate-200"
                onClick={() => selectTab(tab)}
              >
                {tab.connectionName} ({tab.type.toUpperCase()})
                {tab.connectorName ? ` · ${tab.connectorName}` : ""}
                {tab.status === "connecting" ? " …" : ""}
              </button>
              <button
                type="button"
                aria-label={t("closeTab", { name: tab.connectionName })}
                className="text-slate-400 hover:text-red-600"
                onClick={(e) => {
                  e.stopPropagation();
                  closeTab(tab);
                }}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
        {tabs.length >= 2 && (
          <div
            role="group"
            aria-label={t("layout.label")}
            className="flex shrink-0 items-center gap-1 pb-1"
          >
            {/* <span className="hidden text-[10px] uppercase tracking-wide text-slate-400 sm:inline">
              {t("layout.label")}
            </span> */}
            <button
              type="button"
              aria-label={t("layout.single")}
              title={t("layout.single")}
              aria-pressed={effectiveLayout === "single"}
              onClick={() => setLayout("single")}
              className={layoutButtonClass(effectiveLayout === "single")}
            >
              <Square size={14} aria-hidden />
            </button>
            <button
              type="button"
              aria-label={t("layout.columns")}
              title={t("layout.columns")}
              aria-pressed={effectiveLayout === "columns"}
              onClick={() => setLayout("columns")}
              className={layoutButtonClass(effectiveLayout === "columns")}
            >
              <Columns size={14} aria-hidden />
            </button>
            <button
              type="button"
              aria-label={t("layout.rows")}
              title={t("layout.rows")}
              aria-pressed={effectiveLayout === "rows"}
              onClick={() => setLayout("rows")}
              className={layoutButtonClass(effectiveLayout === "rows")}
            >
              <Rows size={14} aria-hidden />
            </button>
            {tabs.length >= 3 && (
              <button
                type="button"
                aria-label={t("layout.grid")}
                title={t("layout.grid")}
                aria-pressed={effectiveLayout === "grid"}
                onClick={() => setLayout("grid")}
                className={layoutButtonClass(effectiveLayout === "grid")}
              >
                <SquaresFour size={14} aria-hidden />
              </button>
            )}
          </div>
        )}
        {onToggleDetail && (
          <div className="flex shrink-0 items-center gap-1 pb-1">
            <button
              type="button"
              aria-label={detailVisible ? t("detail.hide") : t("detail.show")}
              title={detailVisible ? t("detail.hide") : t("detail.show")}
              aria-pressed={detailVisible}
              onClick={onToggleDetail}
              className={`${layoutButtonClass(detailVisible)} flex items-center gap-1.5 whitespace-nowrap px-2`}
            >
              <SidebarSimple size={14} aria-hidden />
            </button>
          </div>
        )}
      </div>
      {/* Every tab stays mounted so its socket/session survives tab
          switches (D2/D3: only the ✕ closes a session). Inactive tabs are
          hidden with CSS instead of unmounted; the whole stage is hidden
          on non-host routes via `showCanvas`. */}
      <div className={showCanvas ? undefined : "hidden"}>
        <div
          ref={gridRef}
          className={effectiveLayout === "single" ? undefined : "grid"}
          style={gridStyle}
        >
          {tabs.map((tab) => {
            const visible = visibleIds.has(tab.sessionId);
            const index = visibleTabs.findIndex(
              (visibleTab) => visibleTab.sessionId === tab.sessionId,
            );
            return (
              <div
                key={tab.sessionId}
                data-testid={`session-view-${tab.sessionId}`}
                className={
                  visible
                    ? effectiveLayout === "single"
                      ? undefined
                      : "min-h-0 min-w-0"
                    : "hidden"
                }
                style={
                  effectiveLayout !== "single" && visible
                    ? ({
                        ...panePlacement(index),
                        "--session-pane-height": "100%",
                      } as React.CSSProperties)
                    : undefined
                }
              >
                <SessionView
                  tab={tab}
                  onStatus={(status, detail) =>
                    patchTab(tab.sessionId, status, detail)
                  }
                  onRetry={() => retryTab(tab)}
                  active={visible && showCanvas}
                  onRetryVia={(connector) => retryTabVia(tab, connector)}
                  onClose={() => {
                    closeTab(tab);
                    if (activeTab?.sessionId === tab.sessionId)
                      setActiveId(null);
                  }}
                />
              </div>
            );
          })}
          {effectiveLayout !== "single" && visibleTabs.length >= 2 && (
            <>
              {effectiveLayout !== "rows" && (
                <div
                  role="separator"
                  aria-orientation="vertical"
                  aria-label={t("layout.resizeColumns")}
                  aria-valuemin={20}
                  aria-valuemax={80}
                  aria-valuenow={Math.round(columnRatio * 100)}
                  tabIndex={0}
                  onPointerDown={startColumnResize}
                  onKeyDown={(event) => {
                    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight")
                      return;
                    event.preventDefault();
                    const delta = event.key === "ArrowLeft" ? -0.03 : 0.03;
                    setColumnRatio((current) => clampRatio(current + delta));
                  }}
                  style={{
                    gridColumn: "2",
                    gridRow: effectiveLayout === "grid" ? "1 / 4" : "1",
                  }}
                  className="group relative z-10 flex touch-none cursor-col-resize items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                >
                  <span className="absolute inset-y-0 left-1/2 w-0.5 -translate-x-1/2 bg-slate-300 transition-colors group-hover:bg-indigo-500 dark:bg-slate-600" />
                </div>
              )}
              {effectiveLayout !== "columns" && (
                <div
                  role="separator"
                  aria-orientation="horizontal"
                  aria-label={t("layout.resizeRows")}
                  aria-valuemin={20}
                  aria-valuemax={80}
                  aria-valuenow={Math.round(rowRatio * 100)}
                  tabIndex={0}
                  onPointerDown={startRowResize}
                  onKeyDown={(event) => {
                    if (event.key !== "ArrowUp" && event.key !== "ArrowDown")
                      return;
                    event.preventDefault();
                    const delta = event.key === "ArrowUp" ? -0.03 : 0.03;
                    setRowRatio((current) => clampRatio(current + delta));
                  }}
                  style={{
                    gridColumn: effectiveLayout === "grid" ? "1 / 4" : "1",
                    gridRow: "2",
                  }}
                  className="group relative z-10 flex touch-none cursor-row-resize items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                >
                  <span className="absolute inset-x-0 top-1/2 h-0.5 -translate-y-1/2 bg-slate-300 transition-colors group-hover:bg-indigo-500 dark:bg-slate-600" />
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <ActiveSessionsBar onAttach={open} />
    </section>
  );
}

function SessionView({
  tab,
  active = true,
  onStatus,
  onRetry,
  onRetryVia,
  onClose,
}: {
  tab: Tab;
  active?: boolean;
  onStatus: (status: Tab["status"], detail?: string) => void;
  onRetry: () => void;
  onRetryVia: (connector: ConnectorOut) => void;
  onClose: () => void;
}) {
  if (tab.type === "web") {
    return <WebSession tab={tab} active={active} onStatus={onStatus} />;
  }
  if (tab.type === "ssh") {
    return (
      <SshTerminal
        tab={tab}
        onStatus={onStatus}
        onClose={onClose}
        onRetry={onRetry}
        onRetryVia={onRetryVia}
      />
    );
  }
  return <GuacSession tab={tab} onStatus={onStatus} onRetry={onRetry} onClose={onClose} />;
}

function GuacSession({
  tab,
  onStatus,
  onRetry,
  onClose,
}: {
  tab: Tab;
  onStatus: (status: Tab["status"], detail?: string) => void;
  onRetry: () => void;
  onClose: () => void;
}) {
  const t = useTranslations("sessions");
  const { resumeTab } = useSessionTabs();
  const ref = useRef<HTMLDivElement | null>(null);
  const statusRef = useRef(onStatus);
  const resumeRef = useRef(resumeTab);
  const closeRef = useRef(onClose);
  const clientRef = useRef<Guacamole.Client | null>(null);
  // Set when guacd ends the session with `disconnect` (e.g. shell exit):
  // the tab closes with the socket instead of showing retry.
  const endedCleanRef = useRef(false);
  // Graphical clipboard (add-desktop-console D10, enable-rdp-clipboard):
  // desktop and RDP sessions sync the clipboard through guacd; VNC stays out
  // and SSH uses xterm. Content never reaches the API and is never logged
  // (SB-27).
  const graphicalClipboard = tab.type === "desktop" || tab.type === "rdp";
  // Clipboard sync is opt-in: a compromised remote host must not be able to
  // plant arbitrary text in the local clipboard without an explicit action.
  const [clipboardOn, setClipboardOn] = useState(false);
  const [clipboardReadError, setClipboardReadError] = useState(false);
  const clipboardOnRef = useRef(false);
  const lastPointerRef = useRef<{ x: number; y: number } | null>(null);
  const pasteToRemoteRef = useRef<((text: string) => void) | null>(null);
  useEffect(() => {
    clipboardOnRef.current = clipboardOn;
  }, [clipboardOn]);
  // Touch devices need the Touchscreen handler (plain Mouse ignores synthetic
  // mouse events after a touch, so taps did nothing) and a focusable input to
  // make the phone's virtual keyboard appear. Lazy initializer, not an effect:
  // this view only mounts client-side (a session tab is created by a user
  // gesture), so there is no server render to mismatch.
  const [isTouch] = useState(
    () =>
      typeof window !== "undefined" &&
      (navigator.maxTouchPoints > 0 || "ontouchstart" in window),
  );
  const keyInputRef = useRef<HTMLTextAreaElement | null>(null);
  // Classification carried by the server's `error` instruction (D3), and any
  // detail already shown for this tab (e.g. the handshake timeout): a close
  // without a known code must not wipe a message the user needs.
  const codeRef = useRef<number | null>(null);
  const detailRef = useRef<string | undefined>(undefined);
  const tRef = useRef(t);
  // Latest tab snapshot for the effect below (kept out of deps on purpose:
  // the socket must survive parent rerenders; only sessionId/ticket
  // changes reopen it).
  const tabRef = useRef(tab);
  // Keep the latest props/subscriptions in refs without writing to them
  // during render (react-hooks/refs): the assignment happens right after
  // commit, before the socket effect below reads them.
  useEffect(() => {
    statusRef.current = onStatus;
    resumeRef.current = resumeTab;
    closeRef.current = onClose;
    tRef.current = t;
    tabRef.current = tab;
  });
  // Tracks the ticket this view instance already opened: a remount with
  // the SAME ticket reuses it only if it never got to OPEN (ticket then
  // likely intact); otherwise the view resumes with a fresh ticket.
  const attemptRef = useRef<{ key: string; opened: boolean } | null>(null);
  const mountedRef = useRef(false);
  // Guacamole cargado bajo demanda (optimize-web-heavy-bundles D1): hasta que
  // el módulo no está disponible no se construye ni socket ni cliente, así que
  // una ruta sin sesión RDP/VNC nunca lo descarga.
  const [guac, setGuac] = useState<GuacamoleModules | null>(null);

  // Touch/mouse helpers exposed by the session toolbar. The right button is
  // explicit because a long-press is not discoverable and some browsers eat
  // it; it uses the last pointer position (or the display centre).
  const pasteFromClipboard = async () => {
    if (!graphicalClipboard || !clipboardOnRef.current) return;
    setClipboardReadError(false);
    const clipboard = navigator.clipboard;
    if (!clipboard?.readText) {
      setClipboardReadError(true);
      return;
    }
    try {
      const text = await clipboard.readText();
      if (text) pasteToRemoteRef.current?.(text);
    } catch {
      setClipboardReadError(true);
    }
  };
  const sendRightClick = () => {
    const client = clientRef.current;
    if (!client || !guac) return;
    const display = client.getDisplay();
    const point = lastPointerRef.current ?? {
      x: Math.round(display.getWidth() / 2),
      y: Math.round(display.getHeight() / 2),
    };
    const down = new guac.Mouse.State();
    down.x = point.x;
    down.y = point.y;
    down.right = true;
    const up = new guac.Mouse.State();
    up.x = point.x;
    up.y = point.y;
    up.right = false;
    client.sendMouseState(down, true);
    client.sendMouseState(up, true);
  };

  useEffect(() => {
    let alive = true;
    loadGuacamole().then(
      (mod) => {
        if (alive) setGuac(mod);
      },
      () => {
        // Un chunk que no carga debe verse: mismo mensaje que un canal que no
        // se pudo establecer, con el botón de reintentar del tab.
        if (!alive) return;
        detailRef.current = tRef.current("wsError.1011");
        statusRef.current("error", detailRef.current);
      },
    );
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const host = ref.current;
    if (!host) return;
    // Diferido (D1): sin el módulo no hay handshake que abrir; el effect se
    // re-ejecuta en cuanto `guac` llega y entonces sí monta la sesión.
    if (!guac) return;
    const cur = tabRef.current;
    // Instrumentación de diagnóstico (D1): instala el hook antes de
    // construir el socket si la URL trae `?debug=ws`.
    ensureWsLog();
    if (!mountedRef.current) {
      mountedRef.current = true;
      if (cur.status !== "connecting") {
        // Remount of an established tab (page navigation): the old socket
        // was closed on unmount; reattach with a fresh ticket. The ticket
        // update below re-runs this effect to open the new socket.
        resumeRef.current(cur);
        return;
      }
    }
    // Status updates after open (open/error/closed): nothing to do here;
    // a later remount resumes through the branch above.
    if (cur.status !== "connecting") return;
    const key = cur.sessionId + ":" + cur.ticket;
    if (attemptRef.current?.key === key && attemptRef.current.opened) return;
    const ticket = cur.ticket.replace(/=+$/, "");
    const ws = new WebSocket(wsUrl(), [`vst-${ticket}`]);
    attemptRef.current = { key, opened: false };
    // Bloque B: si el handshake no abre ni cierra (proxy tragando el
    // upgrade), no quedarse en "Conectando..." eternamente.
    const openTimer = window.setTimeout(() => {
      if (ws.readyState !== WebSocket.OPEN) {
        try {
          ws.close();
        } catch {
          // already gone
        }
        // Remembered so the close that follows (code 1005, no reason) does not
        // replace this message with the generic "Sesión cerrada.".
        detailRef.current = tRef.current("wsTimeout");
        statusRef.current("error", detailRef.current);
      }
    }, 15000);

    const parser = new guac.Parser();
    const tunnel = {
      state: guac.Tunnel.State.CONNECTING,
      uuid: null,
      oninstruction: null as null | ((opcode: string, args: string[]) => void),
      onerror: null,
      connect: () => undefined,
      disconnect: () => ws.close(),
      sendMessage: (...elements: unknown[]) => {
        if (ws.readyState === 1) {
          // fix-remote-session-ws: guacd 1.6.0 cierra la conexión si recibe
          // un `nop` (keepalive del cliente) tras el handshake SSH. El relay
          // TCP+WS no necesita keepalive; la conexión se mantiene por el
          // socket abierto. Se suprime el envío del `nop` a guacd.
          const opcode = elements[0];
          if (elements.length === 1 && opcode === "nop") return;
          ws.send(guac.Parser.toInstruction(elements));
        }
      },
    };
    parser.oninstruction = (opcode: string, args: string[]) => {
      // guacd ends terminated sessions (e.g. shell `exit`) with a
      // `disconnect` instruction before hanging up: the tab can go away
      // on socket close instead of showing the closed panel with retry.
      if (opcode === "disconnect") endedCleanRef.current = true;
      // The API replaces guacd's free-text `error` with `error,<code>,<reason>`
      // (D3). Read it HERE, before handing the instruction to the library:
      // the Guacamole client calls disconnect() on `error` and the socket dies
      // with a local 1005, so the close code never arrives.
      if (opcode === "error") {
        const code = Number(args[0]);
        if (SESSION_ERROR_CODES.includes(code)) codeRef.current = code;
      }
      tunnel.oninstruction?.(opcode, args);
    };
    ws.onmessage = (ev) => {
      try {
        parser.receive(String(ev.data));
      } catch {
        // malformed instruction: the server-side codec guards this
      }
    };
    ws.onclose = (ev) => {
      // Clean destination-ended session (exit from the terminal): the
      // shell is gone, close the tab instead of offering retry on a
      // session that would reopen a fresh shell anyway.
      if (endedCleanRef.current) {
        closeRef.current();
        return;
      }
      // Prefer the classification carried by the `error` instruction; fall back
      // to the close code when the socket died before the reason was sent.
      const code = codeRef.current ?? Number((ev as CloseEvent).code);
      const key = errorCodeKey(code);
      const detail = key ? tRef.current(key) : detailRef.current;
      statusRef.current(detail ? "error" : "closed", detail);
    };

    const client = new guac.Client(tunnel as unknown as Guacamole.Tunnel);
    clientRef.current = client;
    const display = client.getDisplay();
    const displayEl = display.getElement();
    host.replaceChildren(displayEl);
    // Stop the page from scrolling/zooming under a touch drag; the
    // Touchscreen handler calls preventDefault too, but CSS is the hint the
    // compositor needs.
    displayEl.style.touchAction = "none";

    // Graphical clipboard (add-desktop-console D10, enable-rdp-clipboard):
    // desktop and RDP bridge the clipboard through guacd, with a size cap, a
    // per-session toggle and no logging of any content (SB-27). VNC stays out
    // and SSH uses its xterm-native clipboard path.
    // Paste-in: the guacamole keyboard consumes every key, so Ctrl/Cmd+V is
    // intercepted at document capture *before* the library (stopPropagation,
    // no preventDefault) and the browser emits its paste event with
    // `clipboardData` — no clipboard permission needed.
    const clipboardEnabled = tab.type === "desktop" || tab.type === "rdp";
    const CLIPBOARD_MAX = 32768;
    const writeClipboardToRemote = (text: string) => {
      try {
        const stream = client.createClipboardStream("text/plain");
        const writer = new guac.StringWriter(stream);
        writer.sendText(text.slice(0, CLIPBOARD_MAX));
        writer.sendEnd();
      } catch {
        // The session is worth more than the clipboard: never break it.
      }
    };
    pasteToRemoteRef.current = writeClipboardToRemote;
    const onPaste = (event: ClipboardEvent) => {
      if (!clipboardEnabled || !clipboardOnRef.current) return;
      // A focusable div does not always receive the native paste event; the
      // event may target the body. Accept it while the session has focus.
      if (
        document.activeElement !== host &&
        !host.contains(event.target as Node)
      ) {
        return;
      }
      const data = event.clipboardData?.getData("text/plain") ?? "";
      if (!data) return;
      event.preventDefault();
      writeClipboardToRemote(data);
    };
    const interceptPasteShortcut = (event: KeyboardEvent) => {
      if (!clipboardEnabled || !clipboardOnRef.current) return;
      if (event.key.toLowerCase() !== "v") return;
      if (!(event.ctrlKey || event.metaKey)) return;
      if (event.shiftKey || event.altKey) return;
      if (document.activeElement !== host) return;
      // Let the browser produce the paste event; the guacamole keyboard
      // must not see the keydown (it would consume it).
      event.stopPropagation();
    };
    document.addEventListener("paste", onPaste, true);
    document.addEventListener("keydown", interceptPasteShortcut, true);
    if (clipboardEnabled) {
      client.onclipboard = (stream, mimetype) => {
        if (!clipboardOnRef.current) return;
        if (!String(mimetype).startsWith("text/")) return;
        let text = "";
        const reader = new guac.StringReader(stream);
        reader.ontext = (chunk: string) => {
          if (text.length < CLIPBOARD_MAX) {
            text += chunk.slice(0, CLIPBOARD_MAX - text.length);
          }
        };
        reader.onend = () => {
          try {
            navigator.clipboard?.writeText(text).catch(() => undefined);
          } catch {
            // Clipboard unavailable: stay silent, the session is untouched.
          }
        };
      };
    }

    // fix-session-input-resize: teclado asociado al contenedor focuseable.
    // En `document` capturaba teclas de otros inputs y dejaba pasar el
    // scroll (Espacio bajaba la página). El div recibe foco al montar y
    // al clicar; solo entonces viajan las teclas a la sesión.
    host.tabIndex = -1;
    host.onmousedown = () => host.focus();
    host.focus();
    const keyboard = new guac.Keyboard(host);
    // fix-panel-session-agent-behavior (D3): captura total del teclado.
    // Mientras la sesión tiene el foco todo se reenvía a la sesión y se
    // consume (return false = preventDefault en la lib) para que los
    // atajos del navegador (Espacio, flechas, AvPág, Ctrl+R…) no compitan
    // con la terminal. Sin excepciones.
    keyboard.onkeydown = (keysym: number) => handleSessionKey(keysym, (down, k) =>
      client.sendKeyEvent(down, k),
    );
    keyboard.onkeyup = (keysym: number) => {
      client.sendKeyEvent(0, keysym);
    };
    const sendMouseState = (event: unknown) => {
      const mouseEvent = event as Guacamole.Mouse.Event;
      lastPointerRef.current = {
        x: mouseEvent.state.x,
        y: mouseEvent.state.y,
      };
      client.sendMouseState(mouseEvent.state, true);
    };
    const mouse = new guac.Mouse(displayEl);
    mouse.onEach(["mousedown", "mouseup", "mousemove"], sendMouseState);
    // Mobile: the plain Mouse class deliberately ignores the synthetic mouse
    // events that follow a touch, so a tap never reached the remote host.
    // Touchscreen translates tap/drag/long-press into mouse state.
    const touch = new guac.Mouse.Touchscreen(displayEl);
    touch.onEach(["mousedown", "mouseup", "mousemove"], sendMouseState);
    const focusOnTouch = () => host.focus();
    host.addEventListener("touchstart", focusOnTouch, { passive: true });
    // Mobile virtual keyboard: a focusable but invisible textarea. The
    // "Teclado" button focuses it (a user gesture, so iOS/Android show their
    // keyboard); each key is translated to the Guacamole keysym the RDP
    // session expects. The textarea lives offscreen on <body> so the session
    // container's overflow cannot clip it.
    const keyInput = document.createElement("textarea");
    keyInput.setAttribute("aria-hidden", "true");
    keyInput.setAttribute("autocapitalize", "off");
    keyInput.setAttribute("autocomplete", "off");
    keyInput.setAttribute("autocorrect", "off");
    keyInput.setAttribute("spellcheck", "false");
    keyInput.style.position = "fixed";
    keyInput.style.left = "-10000px";
    keyInput.style.top = "0";
    keyInput.style.width = "1px";
    keyInput.style.height = "1px";
    keyInput.style.opacity = "0";
    document.body.appendChild(keyInput);
    keyInputRef.current = keyInput;
    let lastKeydownAt = Number.NEGATIVE_INFINITY;
    const tapKey = (keysym: number) => {
      client.sendKeyEvent(1, keysym);
      client.sendKeyEvent(0, keysym);
    };
    const onMobileKeyDown = (event: KeyboardEvent) => {
      const keysym = keyNameKeysym(event.key);
      if (keysym === null) return; // IME/Unidentified: delivered via input
      event.preventDefault();
      event.stopPropagation();
      tapKey(keysym);
      lastKeydownAt = performance.now();
    };
    const onMobileBeforeInput = (event: InputEvent) => {
      if (event.inputType === "deleteContentBackward") {
        event.preventDefault();
        tapKey(0xff08);
      } else if (event.inputType === "insertLineBreak") {
        event.preventDefault();
        tapKey(0xff0d);
      }
    };
    const onMobileInput = () => {
      const value = keyInput.value;
      keyInput.value = "";
      if (!value) return;
      // Android reports the keydown as "Unidentified" and delivers the text
      // here; iOS already sent it from keydown. Skip the echo.
      if (performance.now() - lastKeydownAt < 120) return;
      for (const ch of value) tapKey(charKeysym(ch));
    };
    keyInput.addEventListener("keydown", onMobileKeyDown);
    keyInput.addEventListener("beforeinput", onMobileBeforeInput);
    keyInput.addEventListener("input", onMobileInput);
    // fix-session-input-resize: la terminal debe seguir al contenedor.
    // El handshake servidor fija 1024x768 y nada volvía a enviar `size`.
    // El RDP intenta adoptar el tamaño (display-update); el escalado local es
    // la red de seguridad: en un móvil el canvas se veía recortado a 1024px.
    const fitDisplay = () => {
      const w = host.clientWidth;
      const h = host.clientHeight;
      const dw = display.getWidth();
      const dh = display.getHeight();
      if (!w || !h || !dw || !dh) return;
      display.scale(Math.min(w / dw, h / dh, 1));
    };
    display.onresize = fitDisplay;
    let sizeTimer: number | null = null;
    const pushSize = () => {
      if (ws.readyState !== 1) return;
      const w = host.clientWidth;
      const h = host.clientHeight;
      // Hidden stage (off-host routes keep the socket alive): zero size
      // would shrink the terminal to the minimum — skip until visible.
      if (w === 0 || h === 0) return;
      client.sendSize(Math.max(200, w), Math.max(200, h));
      fitDisplay();
    };
    const ro =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => {
            if (sizeTimer !== null) window.clearTimeout(sizeTimer);
            sizeTimer = window.setTimeout(pushSize, 150);
          });
    ro?.observe(host);

    ws.onopen = () => {
      window.clearTimeout(openTimer);
      if (attemptRef.current?.key === key) attemptRef.current.opened = true;
      tunnel.state = guac.Tunnel.State.OPEN;
      statusRef.current("open");
      // The guacd handshake already happened server-side; open the client.
      client.connect("");
      pushSize();
    };

    return () => {
      // Unmount (page navigation, tab switch): close our socket so the
      // server applies detach/close semantics; a remount resumes. If the
      // socket never opened, the ticket is likely intact: allow retry.
      window.clearTimeout(openTimer);
      if (sizeTimer !== null) window.clearTimeout(sizeTimer);
      ro?.disconnect();
      keyboard.reset();
      keyboard.onkeydown = null;
      keyboard.onkeyup = null;
      host.onmousedown = null;
      host.removeEventListener("touchstart", focusOnTouch);
      keyInput.removeEventListener("keydown", onMobileKeyDown);
      keyInput.removeEventListener("beforeinput", onMobileBeforeInput);
      keyInput.removeEventListener("input", onMobileInput);
      keyInput.remove();
      if (keyInputRef.current === keyInput) keyInputRef.current = null;
      document.removeEventListener("paste", onPaste, true);
      document.removeEventListener("keydown", interceptPasteShortcut, true);
      client.onclipboard = null;
      display.onresize = null;
      clientRef.current = null;
      if (!attemptRef.current?.opened) attemptRef.current = null;
      try {
        ws.close();
      } catch {
        // already gone
      }
    };
  }, [tab.sessionId, tab.ticket, tab.type, guac]);

  return (
    <div
      data-session-stage
      className="relative flex max-h-[calc(100vh-6rem)] min-h-[260px] flex-col bg-slate-100 dark:bg-slate-950"
      style={{ height: "var(--session-pane-height, 70vh)" }}
    >
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-white/90 px-2 py-1.5 text-xs text-slate-600 dark:border-slate-800 dark:bg-slate-900/90 dark:text-slate-300">
        {graphicalClipboard && (
          <label className="flex items-center gap-1">
            <input
              type="checkbox"
              checked={clipboardOn}
              onChange={(e) => {
                setClipboardOn(e.target.checked);
                setClipboardReadError(false);
              }}
            />
            {t("clipboard.toggle")}
          </label>
        )}
        {graphicalClipboard && (
          <button
            type="button"
            disabled={!guac || !clipboardOn}
            onClick={() => void pasteFromClipboard()}
            className="rounded border border-slate-300 bg-white px-2 py-1 hover:bg-slate-100 disabled:opacity-40 dark:border-slate-700 dark:bg-slate-800 dark:hover:bg-slate-700"
          >
            {t("clipboard.paste")}
          </button>
        )}
        <button
          type="button"
          disabled={!guac}
          onClick={sendRightClick}
          className="rounded border border-slate-300 bg-white px-2 py-1 hover:bg-slate-100 disabled:opacity-40 dark:border-slate-700 dark:bg-slate-800 dark:hover:bg-slate-700"
        >
          {t("rightClick")}
        </button>
        {isTouch && (
          // Focusing the offscreen textarea is what makes the phone's virtual
          // keyboard appear; it must happen inside this click gesture.
          <button
            type="button"
            onClick={() => keyInputRef.current?.focus()}
            className="rounded border border-slate-300 bg-white px-2 py-1 font-medium hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:hover:bg-slate-700"
          >
            {t("keyboard")}
          </button>
        )}
        {clipboardReadError && (
          <span role="status" className="text-amber-700 dark:text-amber-300">
            {t("clipboard.pasteDenied")}
          </span>
        )}
      </div>
      <div className="relative min-h-0 flex-1">
        {/* guac display layers use z-index:-1 internally; isolate so the
            canvas paints above this section's background but stays hidden
            beneath the cursor layer. */}
        <div
          ref={ref}
          data-testid={`session-host-${tab.sessionId}`}
          className="absolute inset-0 isolate z-0 overflow-hidden focus:outline-none"
        />
        {tab.status === "connecting" && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-sm text-slate-400">{t("connecting")}</span>
          </div>
        )}
        {(tab.status === "error" || tab.status === "closed") && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white/95 p-6 text-center dark:bg-slate-900/95">
            <p className="max-w-sm text-sm font-medium text-slate-700 dark:text-slate-200">
              {tab.status === "error" ? (tab.detail ?? t("wsError.1011")) : t("closed")}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onRetry}
                className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white dark:bg-slate-100 dark:text-slate-900"
              >
                {t("retry")}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-slate-300 px-3 py-1.5 text-sm dark:border-slate-700"
              >
                {t("close")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function WebSession({
  tab,
  active = true,
  onStatus,
}: {
  tab: Tab;
  /** False when another pane is active: keep the session alive but unload the
   * external page, which otherwise keeps its timers/workers running hidden. */
  active?: boolean;
  onStatus: (status: Tab["status"], detail?: string) => void;
}) {
  const t = useTranslations("sessions");
  const { resumeTab } = useSessionTabs();
  const resumeRef = useRef(resumeTab);
  const statusRef = useRef(onStatus);
  const tabRef = useRef(tab);
  useEffect(() => {
    resumeRef.current = resumeTab;
    statusRef.current = onStatus;
    tabRef.current = tab;
  });
  const [loaded, setLoaded] = useState(false);
  const timer = useRef<number | null>(null);
  const mountedRef = useRef(false);

  useEffect(() => {
    if (loaded || !tab.url || !active) return;
    timer.current = window.setTimeout(() => {
      // Use the ref, not the render's callback: parent rerenders replace the
      // inline onStatus and used to restart this timer every 4 s.
      statusRef.current("error", t("blockedEmbed"));
    }, 4000);
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [active, loaded, tab.url, t]);

  // Keepalive (fix-app-shell-ux): web sessions have no guacd stream, so
  // without this socket the server sweep reaps them as orphans. The
  // server only touches state on each message. Minimal deps on purpose:
  // parent rerenders must not reopen the socket (single-use ticket).
  useEffect(() => {
    const cur = tabRef.current;
    if (!cur.url || !cur.ticket) return;
    ensureWsLog();
    if (!mountedRef.current) {
      mountedRef.current = true;
      if (cur.status !== "connecting") {
        resumeRef.current(cur);
        return;
      }
    }
    if (cur.status !== "connecting") return;
    const ticket = cur.ticket.replace(/=+$/, "");
    const ws = new WebSocket(wsUrl(), [`vst-${ticket}`]);
    const iv = window.setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) ws.send("ping");
    }, 30000);
    ws.onopen = () => statusRef.current("open");
    return () => {
      window.clearInterval(iv);
      try {
        ws.close();
      } catch {
        // already gone
      }
    };
  }, [tab.sessionId, tab.ticket, tab.url]);

  if (!tab.url) {
    return <div className="p-6 text-sm text-slate-500">{t("noUrl")}</div>;
  }
  return (
    <div
      data-session-stage
      className="relative flex max-h-[calc(100vh-6rem)] min-h-[260px] flex-col"
      style={{ height: "var(--session-pane-height, 70vh)" }}
    >
      {active && (
        <iframe
          src={tab.url}
          title={tab.connectionName}
          loading="lazy"
          className="h-full w-full rounded-lg border border-slate-200"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          referrerPolicy="no-referrer"
          onLoad={() => {
            if (timer.current) window.clearTimeout(timer.current);
            setLoaded(true);
            onStatus("open");
          }}
        />
      )}
      {!loaded && tab.status === "error" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white/95 text-center dark:bg-slate-900/95">
          <p className="max-w-sm text-sm text-slate-600 dark:text-slate-300">
            {tab.detail}
          </p>
          <a
            href={tab.url}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm dark:border-slate-700"
          >
            {t("openNewTab")}
          </a>
        </div>
      )}
    </div>
  );
}

function ActiveSessionsBar({
  onAttach,
}: {
  onAttach: TabsContextValue["open"];
}) {
  const t = useTranslations("sessions");
  const { tabs } = useSessionTabs();
  const [sessions, setSessions] = useState<SessionOut[]>([]);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      const list = await listSessions().catch(() => [] as SessionOut[]);
      if (alive) {
        setSessions(list);
        for (const s of list) tenantColors.set(s.tenant_id, s.tenant_color);
      }
    };
    void tick();
    const id = window.setInterval(tick, 10000);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, []);

  // A server session can be `active` without a local tab after a reload or a
  // dead socket. Show it too, otherwise it keeps consuming the quota and is
  // impossible to recover or close from the UI.
  const openIds = new Set(tabs.map((tab) => tab.sessionId));
  const recoverable = sessions.filter((s) => !openIds.has(s.session_id));
  if (recoverable.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-slate-200 px-3 py-2 text-xs dark:border-slate-800">
      <span className="text-slate-400">{t("recoverable")}</span>
      {recoverable.map((s) => (
        <span
          key={s.session_id}
          className="flex items-center gap-2 rounded-full border px-2 py-0.5"
          style={{ borderColor: s.tenant_color }}
        >
          <span className="text-slate-600 dark:text-slate-300">{s.name}</span>
          <button
            type="button"
            className="text-slate-500 underline"
            onClick={() =>
              void onAttach(
                {
                  id: s.connection_id,
                  tenant_id: s.tenant_id,
                  folder_id: null,
                  name: s.name,
                  type: s.type as ConnectionOut["type"],
                  host: "",
                  port: null,
                  params: null,
                  credential_id: null,
                  agent_id: null,
                },
                s.session_id,
              )
            }
          >
            {t("reattach")}
          </button>
          <button
            type="button"
            className="text-red-500"
            onClick={() => {
              void closeSession(s.session_id)
                .catch(() => undefined)
                .finally(() =>
                  setSessions((prev) =>
                    prev.filter((row) => row.session_id !== s.session_id),
                  ),
                );
            }}
          >
            {t("close")}
          </button>
        </span>
      ))}
    </div>
  );
}
